import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { useAuth, API } from "../App";
import { useI18n } from "../i18n/I18nProvider";
import { useOrg } from "../contexts/OrgProvider";
import { motion } from "framer-motion";
import { TrendingUp, ArrowUp, ArrowDown, Minus, BarChart3 } from "lucide-react";
import { Badge } from "../components/ui/badge";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "../components/ui/card";
import { Progress } from "../components/ui/progress";
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
  ReferenceLine
} from "recharts";
import { PageHeader } from "../components/PageHeader";
import { FW_SHORT as fwNames, FW_COLORS as fwColors } from "../constants/frameworks";

const Benchmarking = () => {
  const { token } = useAuth();
  const { t } = useI18n();
  const { activeFrameworks } = useOrg();
  const fwEntries = Object.entries(fwNames).filter(([id]) => activeFrameworks.includes(id));
  const [summaries, setSummaries] = useState(null);
  const [selectedFw, setSelectedFw] = useState(activeFrameworks[0] || "iso27001");
  const [detail, setDetail] = useState(null);
  const [loading, setLoading] = useState(true);

  const fetchSummaries = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/benchmarking/summary/all`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setSummaries(res.data);
    } catch (error) {
      console.error("Failed:", error);
    }
  }, [token]);

  const fetchDetail = useCallback(async (fw) => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/benchmarking/${fw}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setDetail(res.data);
    } catch (error) {
      console.error("Failed:", error);
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => { fetchSummaries(); }, [fetchSummaries]);
  useEffect(() => {
    if (activeFrameworks.length > 0 && !activeFrameworks.includes(selectedFw)) {
      setSelectedFw(activeFrameworks[0]);
    }
  }, [activeFrameworks, selectedFw]);
  useEffect(() => { fetchDetail(selectedFw); }, [selectedFw, fetchDetail]);

  const getColor = (pct) => pct >= 75 ? "text-green-500" : pct >= 50 ? "text-yellow-500" : "text-red-500";

  const sectorData = detail?.by_sector ? Object.entries(detail.by_sector).map(([k, v]) => ({
    name: k.charAt(0).toUpperCase() + k.slice(1), value: v, fill: v < detail.org_score ? "#22C55E" : "#EF4444"
  })) : [];

  return (
    <div className="p-6 space-y-6" data-testid="benchmarking-page">
      <PageHeader
        title={t("bench.title")}
        description={t("bench.subtitle")}
      />

      {/* Framework Cards */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        {fwEntries.map(([id, name]) => {
          const s = summaries?.[id];
          const isSelected = selectedFw === id;
          const PositionIcon = s?.position === "above_average" ? ArrowUp : s?.position === "below_average" ? ArrowDown : Minus;
          return (
            <motion.div key={id} whileHover={{ scale: 1.02 }} whileTap={{ scale: 0.98 }}>
              <Card className={`border cursor-pointer transition-all ${isSelected ? 'ring-2 ring-primary border-primary' : 'border-border hover:border-primary/50'}`}
                onClick={() => setSelectedFw(id)} data-testid={`bench-fw-${id}`}>
                <CardContent className="pt-4 pb-4">
                  <div className="flex items-center gap-2 mb-2">
                    <div className="w-2.5 h-2.5 rounded-full" style={{ backgroundColor: fwColors[id] }} />
                    <span className="text-xs font-medium">{name}</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className="text-2xl font-heading font-bold">P{s?.percentile || 0}</span>
                    <PositionIcon className={`w-4 h-4 ${s?.position === 'above_average' ? 'text-green-500' : 'text-red-500'}`} />
                  </div>
                  <p className="text-xs text-muted-foreground">{s?.org_score || 0}% vs avg {s?.industry_avg || 0}%</p>
                </CardContent>
              </Card>
            </motion.div>
          );
        })}
      </div>

      {detail && !loading && (
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Percentile & Position */}
          <Card className="border-border" data-testid="bench-score-card">
            <CardContent className="pt-6 text-center">
              <p className="text-sm text-muted-foreground mb-2">{fwNames[selectedFw]}</p>
              <p className={`text-6xl font-heading font-bold ${getColor(detail.percentile)}`}>
                P{detail.percentile}
              </p>
              <p className="text-sm text-muted-foreground mt-2">{t("bench.percentile")}</p>
              <div className="mt-4 space-y-3">
                <div className="flex items-center justify-between text-sm">
                  <span className="text-muted-foreground">{t("bench.your_score")}</span>
                  <span className="font-bold">{detail.org_score}%</span>
                </div>
                <div className="flex items-center justify-between text-sm">
                  <span className="text-muted-foreground">{t("bench.industry_avg")}</span>
                  <span>{detail.industry_avg}%</span>
                </div>
                <div className="flex items-center justify-between text-sm">
                  <span className="text-muted-foreground">{t("bench.median")}</span>
                  <span>{detail.median}%</span>
                </div>
                <div className="flex items-center justify-between text-sm">
                  <span className="text-muted-foreground">{t("bench.top25")}</span>
                  <span>{detail.top_25}%</span>
                </div>
                <div className="flex items-center justify-between text-sm">
                  <span className="text-muted-foreground">{t("bench.top10")}</span>
                  <span>{detail.top_10}%</span>
                </div>
              </div>
              {detail.gap_to_top25 > 0 ? (
                <div className="mt-4 p-3 rounded-lg bg-orange-500/10 text-sm">
                  <span className="text-orange-600 font-medium">+{detail.gap_to_top25}%</span>
                  <span className="text-muted-foreground"> {t("bench.gap_to_top")}</span>
                </div>
              ) : (
                <div className="mt-4 p-3 rounded-lg bg-green-500/10 text-sm">
                  <span className="text-green-600 font-medium">{t("bench.in_top")}</span>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Sector Comparison */}
          <div className="lg:col-span-2 space-y-4">
            <Card className="border-border" data-testid="bench-sector-chart">
              <CardHeader>
                <CardTitle className="text-lg">{t("bench.sector_comparison")}</CardTitle>
                <CardDescription>{t("bench.sector_desc")}</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="h-64">
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart data={sectorData} layout="vertical">
                      <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
                      <XAxis type="number" domain={[0, 100]} stroke="hsl(var(--muted-foreground))" />
                      <YAxis dataKey="name" type="category" width={100} tick={{ fontSize: 12 }} stroke="hsl(var(--muted-foreground))" />
                      <Tooltip contentStyle={{ backgroundColor: "hsl(var(--card))", border: "1px solid hsl(var(--border))", borderRadius: "8px" }} />
                      <ReferenceLine x={detail.org_score} stroke="#3B82F6" strokeDasharray="3 3" label={{ value: `You: ${detail.org_score}%`, fill: "#3B82F6", fontSize: 11 }} />
                      <Bar dataKey="value" radius={[0, 4, 4, 0]}>
                        {sectorData.map((entry, i) => (
                          <motion.rect key={i} fill={entry.fill} />
                        ))}
                      </Bar>
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              </CardContent>
            </Card>

            {/* Distribution visual */}
            <Card className="border-border">
              <CardHeader className="pb-2"><CardTitle className="text-sm">{t("bench.distribution")}</CardTitle></CardHeader>
              <CardContent>
                <div className="relative h-8 bg-muted rounded-full overflow-hidden">
                  <div className="absolute inset-y-0 left-0 bg-red-500/30" style={{ width: `${detail.bottom_25}%` }} />
                  <div className="absolute inset-y-0 bg-yellow-500/30" style={{ left: `${detail.bottom_25}%`, width: `${detail.median - detail.bottom_25}%` }} />
                  <div className="absolute inset-y-0 bg-green-500/30" style={{ left: `${detail.median}%`, width: `${detail.top_25 - detail.median}%` }} />
                  <div className="absolute inset-y-0 bg-green-500/50" style={{ left: `${detail.top_25}%`, width: `${100 - detail.top_25}%` }} />
                  <div className="absolute inset-y-0 w-1 bg-primary" style={{ left: `${detail.org_score}%` }} title={`You: ${detail.org_score}%`} />
                </div>
                <div className="flex items-center justify-between text-[10px] text-muted-foreground mt-1">
                  <span>0%</span>
                  <span>Bottom 25: {detail.bottom_25}%</span>
                  <span>Median: {detail.median}%</span>
                  <span>Top 25: {detail.top_25}%</span>
                  <span>100%</span>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      )}

      {loading && (
        <div className="flex items-center justify-center py-12">
          <div className="w-8 h-8 border-2 border-primary border-t-transparent rounded-full animate-spin" />
        </div>
      )}
    </div>
  );
};

export default Benchmarking;
